import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../redux/actions/growthEngine';
import { ToastContainer, toast } from 'react-toastify';
import UIcon from "../../../assets/images/icons/u-icon.png";
import SpotifyIcon from '../../../assets/images/admin/Spotify.svg';
import AppleIcon from '../../../assets/images/admin/Apple.svg';
import StartIcon from '../../../assets/images/admin/Star.svg';
import AmazonIcon from '../../../assets/images/admin/Amazon.svg';
import GroupIcon from '../../../assets/images/admin/Group-line.svg';
import StreamIcon from '../../../assets/images/icons/stream.svg';
import FanbaseIcon from '../../../assets/images/icons/fanbase.svg';
import BestTrack from '../../../assets/images/icons/best-track.svg';
import Listeners from '../../../assets/images/icons/listeners.svg';
import FollowersIcon from '../../../assets/images/icons/followers.svg';
import MetaIcon from '../../../assets/images/admin/meta.svg';
import InstaIcon from '../../../assets/images/admin/Instagram.png';
import TiktokIcon from '../../../assets/images/admin/Tiktok.png';
import YoutubeIcon from '../../../assets/images/admin/Youtube.png';
import StarOutlinedIcon from '../../../assets/images/admin/Star-outlined.svg';
import SpotifyLineIcon from '../../../assets/images/admin/spotify-lines.svg';
import ButtonIcon from '../../../assets/images/icons/button-icon.svg';
import AreaChart from '../charts/AreaChart';
import { formatNumber, formatFixedAmount, getGoalIconAndName } from '../../../helper/utility';
import { CampaignThumbnail } from '../../../components/CampaignThumbnail';
import { ObjectiveIcon } from './ObjectiveIcon';
import { getCampaignObjectiveLabel, getCampaignTargetLabel } from '../../../helper/campaignDisplay';
import ChatInput from '../AmplitudeAI/Chat/ChatInput';
import "react-toastify/dist/ReactToastify.css";
import { getArtistInfo, sendMessage } from '../../../redux/actions';
import GrowthEngineMetricCard from './GrowthEngineMetricCard';
import ArtistManagerDspLockOverlay from '../ArtistManager/ArtistManagerDspLockOverlay';
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import RandomActionsButton from '../../../components/RandomActionsButon';
import { showToast } from '../../../helper/ShowToast';
import { CreditPlanModal } from '../../../components/CreditPlanModal';
import { ChartSkeleton, MetricValueSkeleton, BestPerformingSongSkeleton, MetaCampaignListSkeleton } from '../CommonSkeleton';
import AppTour from '../../../components/AppTour/AppTour';
import { hasArtistLinked, getUserDisplayName, shouldFetchArtistAnalytics, shouldShowAmplitudeSuggestions } from '../../../helper/access';
import { useMetaCampaignAnalytics, useMetaCampaignList } from '../../../queries/growthEngine/metaCampaign.query';
import { usePlatformChartsBySource, usePlatformStatsBySource } from '../../../queries/dataAnalyst/platformStats.query';
import { useMetaConnectionStatus } from '../../../queries/growthEngine/metaConnection.query';
import { MetaConnectionBanner } from '../../../components/MetaConnectionBanner';
import { growthEngineTourSteps } from '../../../components/AppTour/ModuleTourSteps';
const platforms = [
    { icon: UIcon, alt: "upncomer", name: "all" },
    // { icon: MetaIcon, alt: "Instagram", name: "instagram" },
    // { icon: TiktokIcon, alt: "Tiktok", name: "tiktok" },
    // { icon: YoutubeIcon, alt: "Youtube", name: "youtube" },
];

export const GrowthEngine = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const { hash } = useLocation()
    const [runTour, setRunTour] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = React.useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [selectedPerformancePeriod, setSelectedPerformancePeriod] = React.useState("all");
    const [selectedPlatform, setSelectedPlatform] = useState("all")
    const { formData, goalOptions } = useSelector(state => state.growthEngine);
    const authToken = useSelector((state) => state.auth.token);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const { artistInfo } = useSelector(state => state?.artistDetails)
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const hasDspMetricsAccess = useMemo(
        () => shouldFetchArtistAnalytics(access, userDetails) || (!access && hasActiveSubscription),
        [access, userDetails, hasActiveSubscription]
    );
    const lockDspMetrics = !hasDspMetricsAccess;
    const needsLinkArtist = !hasArtistLinked(access, userDetails);
    const showDspCardLinkArtistCta = lockDspMetrics && needsLinkArtist;
    const showDspCardSubscribeCta =
        lockDspMetrics && !needsLinkArtist && !hasActiveSubscription;

    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );

    const hasInsightsAccess = useMemo(
        () => shouldShowAmplitudeSuggestions(access, userDetails, { hasActiveSubscription }),
        [access, userDetails, hasActiveSubscription]
    );
    const lockInsights = !hasInsightsAccess;
    const showInsightsLinkArtistCta = lockInsights && needsLinkArtist;

    const metaAccountKey =
        userDetails?.email || userDetails?.assignedArtistSongstateId || authToken || null;
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const metaCampaignUserKey = metaAccountKey
        ? `${metaAccountKey}:${activeArtistProfileId || "workspace"}`
        : null;

    const {
        data: metaConnectionStatus,
        isLoading: isMetaConnectionLoading,
    } = useMetaConnectionStatus({
        userKey: metaAccountKey,
        enabled: !!authToken && !!metaAccountKey,
    });

    const {
        data: metaCampaignListData,
        isLoading: isMetaCampaignListLoading,
    } = useMetaCampaignList({
        userKey: metaCampaignUserKey,
        page: 1,
        limit: 10,
        enabled: !!authToken && !!metaCampaignUserKey,
    });

    const metacampaignList = metaCampaignListData?.campaigns ?? [];
    const metaCampaignLiveDataAvailable =
        metaCampaignListData?.metaLiveDataAvailable ?? true;

    const {
        data: metaCampaignAnalytics,
        isLoading: isCampaignAnalyticsLoading,
    } = useMetaCampaignAnalytics({
        userKey: metaCampaignUserKey,
        range: selectedPerformancePeriod,
        enabled: !!authToken && !!metaCampaignUserKey,
    });
    const campaignAnalyticLoading = isCampaignAnalyticsLoading && !metaCampaignAnalytics;

    const {
        data: platformStatsBySource,
        isLoading: isPlatformStatsLoading,
    } = usePlatformStatsBySource({
        userKey: metaCampaignUserKey,
        type: selectedPerformancePeriod,
        source: selectedPlatform,
        enabled: hasDspMetricsAccess && !!authToken && !!metaCampaignUserKey,
    });

    const {
        data: platformChartStatsBySource,
        isLoading: isPlatformChartsLoading,
    } = usePlatformChartsBySource({
        userKey: metaCampaignUserKey,
        type: selectedPerformancePeriod,
        source: selectedPlatform,
        enabled: hasDspMetricsAccess && !!authToken && !!metaCampaignUserKey,
    });
    const dspPlatformLoading =
        (isPlatformStatsLoading && !platformStatsBySource) ||
        (isPlatformChartsLoading && !platformChartStatsBySource);

    useEffect(() => {
        if(hash === "#start-tour"){
            setRunTour(true)
        }
    }, [hash])

    useEffect(() => {
        if (isMetaConnectionLoading || !metaConnectionStatus) return;

        const hasStaleMetaUi =
            formData.metaLoggedIn ||
            formData.facebookUserId ||
            formData.pageList?.length ||
            formData.adAccountList?.length ||
            formData.adAccountId;

        // API says not connected / needs reconnect — drop any previous user's Redux Meta UI
        if (metaConnectionStatus.needsReconnect || !metaConnectionStatus.connected) {
            if (hasStaleMetaUi) {
                dispatch(actions.clearMetaConnectionState());
            }
            return;
        }

        const hasValidMetaToken =
            metaConnectionStatus.connected &&
            (metaConnectionStatus.tokenStatus === 'valid' ||
                metaConnectionStatus.tokenStatus === 'expiring_soon');

        if (
            hasValidMetaToken &&
            (!formData.metaLoggedIn || !formData.facebookUserId)
        ) {
            dispatch(actions.getCurrentFacebookAccount(toast));
        }
    }, [
        metaConnectionStatus,
        isMetaConnectionLoading,
        formData.metaLoggedIn,
        formData.facebookUserId,
        formData.pageList?.length,
        formData.adAccountList?.length,
        formData.adAccountId,
        dispatch,
    ]);

    useEffect(() => {
        if (!hasDspMetricsAccess) return;
        dispatch(getArtistInfo(toast));
    }, [hasDspMetricsAccess, dispatch]);

    const formatDate = (dateString, type) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();

        const getOrdinalSuffix = (d) => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const formatted = `${day}${getOrdinalSuffix(day)} ${month} ${year}`;

        if (type === 'start') {
            return `Started ${formatted}`;
        }

        const hasEnded = date.getTime() <= Date.now();
        return hasEnded ? `Ended ${formatted}` : `Ends on ${formatted}`;
    };

    const handleViewHistory = (platform) => {
        dispatch(actions.selectCampaignListPlatform(platform))
        navigate('/admin/growth-engine/campaign-history')
    }

    const handleClickInsights = (value, ctaOptions) => {
        let updateData = {
            objective: value,
            callToAction: ctaOptions,
            platformType: '',
            shouldShowFeatureFM: value === "Playlist Growth" ? true : false
        }
        dispatch(actions.updateFormData(updateData));
        navigate('/admin/growth-engine/campaign')
    };

    // console.log(metacampaignList, 'metacampaignList')
    const handleSend = (input) => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);   // Open subscription modal
            return;
        }
        //BLOCK if no credits
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true); // Open credit purchase modal
            return;
        }
        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    const handleLinkArtistClick = useCallback(() => {
        showToast("info", "Link your artist profile to unlock artist insights.");
        navigate("/link-accounts");
    }, [navigate]);

    const handleSubscribeClick = useCallback(() => {
        showToast("info", "Please subscribe to unlock artist insights.");
        setShowPricingPlanModal(true);
    }, []);

    const onPerformancePeriodChange = useCallback(
        (event) => {
            const period = event.target.value;
            setSelectedPerformancePeriod(period);
        },
        []
    );

    const onSocialPlatformChange = useCallback(
        (value) => {
            if (value === 'youtube' || value === 'tiktok') {
                showToast('info', 'Work in Progress')
            } else {
                setSelectedPlatform(value);
            }
        },
        []
    );

    const followersChart = useMemo(() => ({
        name: 'Total Followers',
        categories: platformChartStatsBySource?.totalFollowers?.map(entry => entry.date) || [],
        data: platformChartStatsBySource?.totalFollowers?.map(entry => entry.followers_total) || [],
        intervalType: selectedPerformancePeriod
    }), [platformChartStatsBySource, selectedPerformancePeriod]);

    const ExpenditureChart = useMemo(() => ({
        name: 'Total Expenditure',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.spendUsd) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);

    const ImpressionsChart = useMemo(() => ({
        name: 'Impression',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.impressions) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);

    const CpcChart = useMemo(() => ({
        name: 'Cost-Per-Fan',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.cpc) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);

    return (
        <div className='GrowthEngine-page py-5'>
            <AppTour
                tourSteps={growthEngineTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-5 p-4 lg:p-5">
                    <div className='flex flex-wrap items-center justify-between gap-[11px]'>
                        <h5 className='flex items-center text-white text-[23px] font-manrope font-bold leading-tight'>Your Growth Engine <i className="bi bi-bar-chart-line-fill text-4xl leading-none ms-8"></i></h5>
                        <button className='btn-custom2 w-[192px] flex items-center justify-center gap-5 shadow-lg' onClick={() => navigate('/admin/growth-engine/campaign')}>New Campaign <i className='bi bi-plus-circle text-[20px]'></i></button>
                    </div>
                </div>

                <MetaConnectionBanner
                    metaConnectionStatus={metaConnectionStatus}
                    isLoading={isMetaConnectionLoading}
                    showSavedCampaignsNote={!metaCampaignLiveDataAvailable}
                />

                {/* Performance Section */}
                <div className="performance-section bg-theme_clr1 rounded-xl mb-5 p-4 lg:px-5 pt-[26px] pb-[30px]" data-tour="artist-statistics-performance">
                    <div className="header-section flex flex-wrap items-center justify-between gap-2 mb-[10px] md:mb-[21px]">
                        <div className="flex flex-wrap gap-x-6 gap-y-3">
                            <select
                                value={selectedPerformancePeriod}
                                onChange={onPerformancePeriodChange}
                                className="appearance-none bg-theme_clr1 text-white text-base font-manrope border-0 focus:outline-none pr-8" style={{ WebkitAppearance: "none", MozAppearance: "none", appearance: "none" }}>
                                <option value="all">All time statistics</option>
                                <option value="weekly">Weekly statistics</option>
                                <option value="monthly">Monthly statistics</option>
                                <option value="yearly">Yearly statistics</option>
                            </select>
                            <div className="icons flex items-center gap-6">
                                {platforms.map((platform) => (
                                    <img
                                        key={platform.alt}
                                        src={platform.icon}
                                        alt={platform.alt}
                                        onClick={() => onSocialPlatformChange(platform.name)}
                                        className={`block w-[30px] h-[30px] cursor-pointer ${platform.name === "youtube" || platform.name === "tiktok" ? "opacity-50" : ''} ${selectedPlatform === platform.name
                                            ? "ring-4 ring-orange-500 squared-full rounded"
                                            : ""
                                            }`}
                                    />
                                ))}
                            </div>

                        </div>
                        <button type='button' className='btn-custom3' onClick={() => handleViewHistory('Meta')}>Manage Campaigns</button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 2xl:gap-[22px] flex-nowrap overflow-auto">
                        <div className="grid-item">
                            <GrowthEngineMetricCard
                                locked={lockDspMetrics}
                                lockIcon={FollowersIcon}
                                lockLabel="Followers"
                                lockPreviewValue="132k"
                                lockChartType="area"
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                showSubscribeCta={showDspCardSubscribeCta}
                                onLinkArtist={handleLinkArtistClick}
                                onSubscribe={handleSubscribeClick}
                            >
                                <div className="icon mb-1">
                                    <img src={FollowersIcon} alt="Followers" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: '-10px' }}>
                                    {!lockDspMetrics && dspPlatformLoading ? (
                                        <ChartSkeleton />
                                    ) : followersChart.categories.length > 0 ? (
                                        <AreaChart key={selectedPerformancePeriod} state={followersChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    {!lockDspMetrics && dspPlatformLoading ? (
                                        <MetricValueSkeleton />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(platformStatsBySource?.totalFollowers)}</div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Followers</div>
                                </div>
                            </GrowthEngineMetricCard>
                        </div>
                        <div className="grid-item">
                            <GrowthEngineMetricCard locked={false}>
                                <div className="icon mb-1">
                                    <img src={FanbaseIcon} alt="Cost-Per-Fan" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {campaignAnalyticLoading ? (
                                        <ChartSkeleton />
                                    ) : metaCampaignAnalytics?.timeline?.length > 0 ? (
                                        <AreaChart
                                            key={selectedPerformancePeriod}
                                            state={CpcChart}
                                        />
                                    ) : (
                                        <div
                                            className="flex items-center justify-center text-sm font-bold text-gray-500"
                                            style={{ height: "120px" }}
                                        >
                                            No CPC chart available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <MetricValueSkeleton />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                            {metaCampaignAnalytics?.totals?.currency
                                                ? `${metaCampaignAnalytics.totals.currency} ${metaCampaignAnalytics?.totals?.cpc || 0}`
                                                : (metaCampaignAnalytics?.totals?.cpc || 0)}
                                        </div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Cost-Per-Fan</div>
                                </div>
                            </GrowthEngineMetricCard>
                        </div>
                        <div className="grid-item">
                            <GrowthEngineMetricCard
                                locked={lockDspMetrics}
                                lockIcon={BestTrack}
                                lockLabel="Best Performing Song"
                                lockPreviewValue="330k"
                                lockChartType="image"
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                showSubscribeCta={showDspCardSubscribeCta}
                                onLinkArtist={handleLinkArtistClick}
                                onSubscribe={handleSubscribeClick}
                            >
                                <div className="icon">
                                    <img src={BestTrack} alt="Best Track" />
                                    {!lockDspMetrics && dspPlatformLoading ? (
                                        <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200" />
                                    ) : (
                                        <div className="text-black text-[12.26px] font-bold mt-2">{platformStatsBySource?.topTrack?.track_name}</div>
                                    )}
                                </div>
                                <div className="chart-wrapper">
                                    {!lockDspMetrics && dspPlatformLoading ? (
                                        <BestPerformingSongSkeleton />
                                    ) : (
                                        <img src={platformStatsBySource?.topTrack?.image_url} alt="Best Track" className="block w-[400px] mx-auto" />
                                    )}
                                </div>
                                <div className="cover">
                                    {!lockDspMetrics && dspPlatformLoading ? (
                                        <MetricValueSkeleton />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(platformStatsBySource?.topTrack?.streamsCount)} Views</div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Best Performing Song</div>
                                </div>
                            </GrowthEngineMetricCard>
                        </div>
                        <div className="grid-item">
                            <GrowthEngineMetricCard locked={false}>
                                <div className="icon mb-1">
                                    <i className="bi bi-currency-dollar text-4xl leading-none"></i>
                                </div>
                                {campaignAnalyticLoading ? (
                                    <ChartSkeleton />
                                ) : metaCampaignAnalytics?.timeline?.length > 0 ? (
                                    <AreaChart key={selectedPerformancePeriod} state={ExpenditureChart} />
                                ) : (
                                    <div
                                        className="flex items-center justify-center text-sm font-bold text-gray-500"
                                        style={{ height: "120px" }}
                                    >
                                        No Expenditure chart available
                                    </div>
                                )}
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <MetricValueSkeleton />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                            ${formatFixedAmount(metaCampaignAnalytics?.totals?.spendUsd) ?? "0.00"}
                                            {Number(formatFixedAmount(metaCampaignAnalytics?.totals?.spend)) > 0 && metaCampaignAnalytics?.totals?.currency
                                                ? ` (${metaCampaignAnalytics.totals.currency} ${formatFixedAmount(metaCampaignAnalytics.totals.spend)})`
                                                : ""}
                                        </div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Expenditure</div>
                                </div>
                            </GrowthEngineMetricCard>
                        </div>
                        <div className="grid-item sm:col-span-2 xl:col-span-1">
                            <GrowthEngineMetricCard locked={false}>
                                <div className="icon mb-1">
                                    <img src={StreamIcon} alt="Streams" />
                                </div>
                                {campaignAnalyticLoading ? (
                                    <ChartSkeleton />
                                ) : metaCampaignAnalytics?.timeline?.length > 0 ? (
                                    <AreaChart key={selectedPerformancePeriod} state={ImpressionsChart} />
                                ) : (
                                    <div
                                        className="flex items-center justify-center text-sm font-bold text-gray-500"
                                        style={{ height: "120px" }}
                                    >
                                        No Impressions chart available
                                    </div>
                                )}
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <MetricValueSkeleton />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(metaCampaignAnalytics?.totals?.impressions) || 0}</div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Impressions</div>
                                </div>
                            </GrowthEngineMetricCard>
                        </div>
                    </div>
                </div>
                <div className='mb-4' data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={`Launch campaigns to grow your fanbase, ${displayName}.`}
                        inputPlaceholder={'Use Amplitude in crafting the perfect campaign based on your vision.'}
                        onSend={handleSend}
                        disabled={showLoad}
                    />
                </div>

                {/* Grid Listing Section */}
                <div className="performance-section">
                    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div className="grid-item" data-tour="meta-campaigns">
                            <div className="info-box h-full bg-theme_clr1 text-white flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight mb-4">
                                    <img src={MetaIcon} alt="Meta" className='inline' />Meta Campaigns
                                </div>
                                {isMetaCampaignListLoading ? (
                                    <MetaCampaignListSkeleton count={3} />
                                ) : (
                                    metacampaignList.length > 0 ? (
                                        metacampaignList.slice(0, 3).map((campaign) => {
                                            const goalMatch = getGoalIconAndName(campaign.name, goalOptions); // ✅ Match goal based on campaign name
                                            const objectiveLabel = getCampaignObjectiveLabel(campaign, goalMatch);
                                            return (
                                                <div key={campaign._id} className="card-wrapper font-lato mb-3">
                                                    <div className="song-card w-full flex items-stretch rounded-sm overflow-hidden mb-1">
                                                        <CampaignThumbnail
                                                            campaign={campaign}
                                                            goalMatch={goalMatch}
                                                            className="w-[60px] min-w-[60px] h-[60px]"
                                                            iconClassName="w-full h-full object-cover"
                                                        />
                                                        <div className="info min-w-0 flex-1 bg-white text-black flex flex-col justify-center px-3 py-2">
                                                            <div className="song-name text-[15px] font-semibold leading-tight truncate">
                                                                {getCampaignTargetLabel(campaign)}
                                                            </div>
                                                            <div className="singer-name text-sm leading-tight truncate">
                                                                {
                                                                    campaign?.targetObject?.artistName || artistInfo?.name
                                                                }

                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="info text-[15px] flex items-start justify-between gap-2">
                                                        <div className="left min-w-0 flex-1">
                                                            <div className='capitalize'>{objectiveLabel}</div>
                                                            {(campaign.endTime || campaign.startTime) && (
                                                                <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">
                                                                    {campaign.endTime
                                                                        ? formatDate(campaign.endTime, 'end')
                                                                        : formatDate(campaign.startTime, 'start')}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="right flex items-center justify-center pt-[2px]">
                                                            {goalMatch ? (
                                                                <ObjectiveIcon
                                                                    icon={goalMatch.icon}
                                                                    name={goalMatch.name}
                                                                    variant="white"
                                                                    className="w-4 h-4"
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-10 text-gray-400">No campaigns found.</div>
                                    )
                                )}
                                {/* <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Increase Streams Campaign</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Ended FEB 14</div>
                                        </div>
                                        <div className="right">
                                            <img src={SpotifyLineIcon} alt="Spotify" className='mt-1' />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Increase Instagram Followers</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Dec 15</div>
                                        </div>
                                        <div className="right">
                                            <img src={InstaIcon} alt="Instagram" className='w-[24px] mt-1' />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">late night drives clear the mind</div>
                                            <div className="singer-name text-sm capitalize truncate">DREAMSNDELUSIONS</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrapc justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow Playlist Campaign</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Nov 15</div>
                                        </div>
                                        <div className="right">
                                            <img src={SpotifyLineIcon} alt="Spotify" className='mt-1' />
                                        </div>
                                    </div>
                                </div> */}
                                <div className="btn-wrapper">
                                    <button type='button' className='btn-custom4' onClick={() => handleViewHistory('Meta')}>View History</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="tiktok-campaigns">
                            <div className="info-box h-full bg-theme_clr1 text-white flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight mb-4">
                                    <img src={TiktokIcon} alt="Tiktok" className='block w-[34px]' />TikTok Campaigns
                                </div>
                                <div className="text-center py-10 text-gray-400"><span>Work In Progress <i class="bi bi-wrench"></i></span></div>
                                {/* <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold lowercase truncate">www.karanmusic.art</div>
                                            <div className="singer-name text-sm capitalize truncate">CASHRULES Tee</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Increase Merch Sales</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Jul 14</div>
                                        </div>
                                        <div className="right">
                                            <i className="bi bi-globe text-2xl"></i>
                                        </div>
                                    </div>
                                </div> */}
                                {/* <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow Pre-Saves Campaign</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">NOV 15</div>
                                        </div>
                                        <div className="right">
                                            <i className="bi bi-download text-2xl"></i>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">@wrldkaran</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow TikTok followers</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Nov 15</div>
                                        </div>
                                        <div className="right">
                                            <img src={TiktokIcon} alt="Tiktok" className='block w-[24px]' />
                                        </div>
                                    </div>
                                </div> */}
                                <div className="btn-wrapper">
                                    <button type='button' className='btn-custom4'>View History</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="youtube-campaigns">
                            <div className="info-box h-full bg-theme_clr1 text-white flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight mb-4">
                                    <img src={YoutubeIcon} alt="Youtube" className='block w-[34px]' />YouTube Campaigns
                                </div>
                                <div className="text-center py-10 text-gray-400"><span>Work In Progress <i class="bi bi-wrench"></i></span></div>
                                {/* <p className="float-right text-white text-xs font-normal">*This section is in Test mode, Pro Version will be available soon.</p> */}
                                {/* <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow Subscribers</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Jul 14</div>
                                        </div>
                                        <div className="right">
                                            <img src={YoutubeIcon} alt="Youtube" className='block w-[32px]' />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow Video Views</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Nov 15</div>
                                        </div>
                                        <div className="right">
                                            <img src={YoutubeIcon} alt="Youtube" className='block w-[32px]' />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-wrapper font-lato mb-3">
                                    <div className="song-card w-full inline-flex items-center rounded-sm overflow-hidden mb-1">
                                        <img src={require('../../../assets/images/Aditya-Ojha.png')} alt="ProfessorU" className='w-[60px] min-w-[60px] h-[60px] object-cover' />
                                        <div className="info bg-white text-black truncate px-3 py-2">
                                            <div className="song-name text-[15px] font-semibold capitalize truncate">This is dummy song</div>
                                            <div className="singer-name text-sm capitalize truncate">Karan Thapar</div>
                                        </div>
                                    </div>
                                    <div className="info text-[15px] flex flex-wrap justify-between gap-1">
                                        <div className="left">
                                            <div className='capitalize'>Grow Shorts Views</div>
                                            <div className="date text-[#ABABAB] text-xs uppercase tracking-[2.4px]">Nov 15</div>
                                        </div>
                                        <div className="right">
                                            <img src={YoutubeIcon} alt="Youtube" className='block w-[32px]' />
                                        </div>
                                    </div>
                                </div> */}
                                <div className="btn-wrapper">
                                    <button type='button' className='btn-custom4'>View History</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="amplitude-insights">
                            <div className="info-box relative h-full bg-theme_clr1 text-white rounded-xl p-4 xl:p-5">
                                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight mb-6">
                                    <img src={require('../../../assets/images/admin/Amplitude-Info.png')} alt="Amplitude Info" className='w-[30px] h-[30px]' />Amplitude Insight
                                </div>
                                <div className="desc-wrapper relative flex min-h-[220px] flex-col gap-6">
                                    <div
                                        className={
                                            lockInsights
                                                ? "pointer-events-none select-none blur-sm"
                                                : ""
                                        }
                                    >
                                        {lockInsights ? (
                                            <div className="space-y-3">
                                                {[
                                                    "Grow your own Playlist",
                                                    "Run Streams Conversion",
                                                    "Run a Pre-Save Campaign",
                                                ].map((label) => (
                                                    <div key={label} className="p-4 text-center">
                                                        <p className="mb-2 text-sm font-bold text-white/80">
                                                            AI-powered campaign recommendation
                                                        </p>
                                                        <div className="btn-custom3 w-full !py-3 opacity-70">
                                                            {label}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <RandomActionsButton count={3} queriesEnabled />
                                        )}
                                    </div>
                                    {lockInsights && (
                                        <ArtistManagerDspLockOverlay
                                            needsLinkArtist={showInsightsLinkArtistCta}
                                            onLinkArtist={handleLinkArtistClick}
                                            onSubscribe={handleSubscribeClick}
                                            title={showInsightsLinkArtistCta ? "Add Your Artist" : "Unlock AI Insights"}
                                            description={
                                                showInsightsLinkArtistCta
                                                    ? "Link your artist profile to unlock AI-powered campaign insights."
                                                    : "Subscribe to unlock AI-powered campaign insights."
                                            }
                                            buttonLabel={
                                                showInsightsLinkArtistCta
                                                    ? "Link Your Artist"
                                                    : "Subscribe to unlock"
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/growth-engine"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
